import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RiskNote } from "../components/layout/RiskDisclaimer";
import { Lock } from "lucide-react";

export default function Checkout() {
  const { t, lang } = useI18n();
  const { spvId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [spv, setSpv] = useState(null);
  const [amount, setAmount] = useState(1000);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get(`/spvs/${spvId}`).then((r) => {
      setSpv(r.data);
      setAmount(r.data.min_ticket);
    }).catch(() => toast.error("SPV introuvable"));
    api.get("/payment/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [spvId]);

  if (!spv) return <div className="section"><div className="container-page text-slate-500">Chargement…</div></div>;

  if (user?.kyc_status !== "qualified") {
    return (
      <div className="section">
        <div className="container-page max-w-2xl">
          <h1 className="font-display text-3xl font-bold text-slate-900">Qualification requise</h1>
          <p className="mt-4 text-slate-600">Vous devez compléter votre qualification investisseur avant de souscrire.</p>
          <Button className="mt-6 rounded-sm bg-slate-900 hover:bg-slate-800" onClick={() => nav("/qualification")} data-testid="go-qualif">Compléter</Button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (amount < spv.min_ticket) return toast.error(`Minimum ${spv.min_ticket}€`);
    setLoading(true);
    try {
      const { data } = await api.post("/payment/checkout", {
        spv_id: spv.spv_id,
        amount: Number(amount),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de création du paiement");
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="section border-b border-slate-200">
        <div className="container-page">
          <div className="label-divider mb-6">{lang === "fr" ? "Souscription" : "Subscription"}</div>
          <h1 className="font-display text-4xl font-black tracking-tight text-slate-900" data-testid="checkout-title">{spv.name}</h1>
          <p className="mt-3 text-slate-600">{spv.hardware} · {spv.industry}</p>
        </div>
      </section>

      <section className="section">
        <div className="container-page grid lg:grid-cols-12 gap-10">
          <form onSubmit={submit} className="lg:col-span-7 bg-white border border-slate-200 rounded-sm p-8 space-y-8" data-testid="checkout-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-600 font-bold mb-3 block">
                {lang === "fr" ? "Montant à investir" : "Amount to invest"}
              </Label>
              <div className="flex items-baseline gap-4">
                <Input
                  type="number"
                  min={spv.min_ticket}
                  max={200000}
                  step={500}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value) || spv.min_ticket)}
                  className="rounded-sm font-mono text-2xl h-14 w-48"
                  data-testid="checkout-amount"
                />
                <span className="text-slate-500">€</span>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                {lang === "fr" ? "Entre" : "Between"} {spv.min_ticket.toLocaleString()}€ {lang === "fr" ? "et" : "and"} 200 000€
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6 space-y-3 text-sm">
              <Row k={t.invest.target} v={`${spv.target_yield}% / ${lang === "fr" ? "an cible" : "y target"}`} />
              <Row k={t.invest.horizon} v={`${spv.horizon_months} ${lang === "fr" ? "mois" : "months"}`} />
              <Row k={t.invest.min} v={`${spv.min_ticket.toLocaleString()}€`} />
              <Row k={lang === "fr" ? "Paiement" : "Payment"} v="Stripe · Carte · Apple Pay · Google Pay" />
            </div>

            {config?.mode === "test" && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-slate-800 px-3 py-2 rounded-sm" data-testid="stripe-test-banner">
                <strong className="text-amber-700">Mode test Stripe :</strong> {lang === "fr"
                  ? "utilisez la carte 4242 4242 4242 4242 (date future, CVC quelconque)."
                  : "use card 4242 4242 4242 4242 (future date, any CVC)."}
              </div>
            )}
            {config?.mode === "live" && (
              <div className="text-xs bg-emerald-50 border border-emerald-200 text-slate-800 px-3 py-2 rounded-sm flex items-center gap-2" data-testid="stripe-live-banner">
                <span className="live-dot" />
                <span><strong className="text-emerald-800">Paiement réel Stripe :</strong> {lang === "fr" ? "votre carte sera débitée immédiatement." : "your card will be charged immediately."}</span>
              </div>
            )}

            <RiskNote />

            <Button type="submit" disabled={loading} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 h-12" data-testid="checkout-submit">
              <Lock className="h-4 w-4 mr-2" />
              {loading ? "…" : (lang === "fr" ? `Payer ${amount.toLocaleString()}€` : `Pay €${amount.toLocaleString()}`)}
            </Button>
          </form>

          <aside className="lg:col-span-5">
            <div className="bg-slate-900 text-white rounded-sm p-8">
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-400 font-bold">Process</div>
              <ol className="mt-6 space-y-5">
                {[
                  ["1", lang === "fr" ? "Paiement sécurisé via Stripe" : "Secure payment via Stripe"],
                  ["2", lang === "fr" ? "Validation conformité (compliance review)" : "Compliance review"],
                  ["3", lang === "fr" ? "Signature documentaire et affectation au SPV" : "Documents signed, SPV allocation"],
                  ["4", lang === "fr" ? "Coupons semestriels capitalisés sur compte interne InvestGPU" : "Semi-annual coupons capitalised on internal InvestGPU account"],
                ].map(([n, l]) => (
                  <li key={n} className="flex gap-4">
                    <span className="font-mono text-emerald-400 text-sm">{n}</span>
                    <span className="text-sm text-slate-300">{l}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-slate-700">
      <span className="text-slate-500">{k}</span>
      <span className="font-medium text-slate-900">{v}</span>
    </div>
  );
}
